<template>
  <div class="overview">
    <FollowUpRequests v-if="isOperatorDashboard" />
    <PartnerBudgetCard v-if="isPartnerDashboard" />
    <ul class="row">
      <li
        v-for="(widget, index) in availableWidgetCards"
        :key="index"
        class="col-xl-3 col-lg-4 col-sm-12 card-item"
        :class="isClickable(widget) ? 'clickable' : 'nonclickable'"
      >
        <router-link :to="getWidgetPath(widget)">
          <stats-card
            class="clickable"
            :icon="widget.icon"
            :title="widget.name"
            :description="widget.description"
            :sub-item-list="widget.subItemList"
            :index="index"
          />
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, nextTick, onMounted, defineAsyncComponent } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import queryString from 'query-string';

import util from '@/Utils/util';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { usePartner } from '@/use/partner';
import { SidebarItemType } from '@/models/app';

const StatsCard = defineAsyncComponent(() => import('@/components/UIComponents/Cards/StatsCard.vue') as any);
const FollowUpRequests = defineAsyncComponent(() => import('@/components/Main/FollowUpRequests.vue') as any);
const PartnerBudgetCard = defineAsyncComponent(() => import('@/components/Main/Partner/PartnerBudgetCard.vue') as any);

const router = useRouter();
const route = useRoute();

const { dashboardCategory, isFamilyDashboard, isPartnerDashboard, isOperatorDashboard } = useDashboard();
const { customerPhoneNumber, excludedPages } = useCustomer();

const { partnerId, partnerCode } = usePartner();

const widgetCards: Ref<SidebarItemType[]> = ref([]);

const getWidgetTargetPath = (widget: SidebarItemType) => widget.children?.[0]?.path || widget.path;

const availableWidgetCards = computed(() => {
  return widgetCards.value.filter((item: SidebarItemType) => {
    if (item.isOnlyMenu || !getWidgetTargetPath(item)) {
      return false;
    }

    return !item.children || item.children.length > 0;
  });
});

const getWidgetPath = (widget: SidebarItemType) => {
  const path = getWidgetTargetPath(widget);
  return !customerPhoneNumber.value || isFamilyDashboard.value
    ? path
    : `${path}?${queryString.stringify({ phoneNumber: customerPhoneNumber.value })}`;
};

const isClickable = (widget: SidebarItemType) => {
  if (isPartnerDashboard.value) {
    return true;
  }

  if (widget.children && widget.children.length > 0) {
    return false;
  }

  return true;
};

const redirectToSpecificFamilyDashboardPage = () => {
  /*
      There are 2 ways to redirect to a specific family dashboard page
      1. Go to specific page based on the button clicked on the webflow registration confirmation page - ENG-4707/5244/5246
      2. Go to scheduled ride form by clicking the link in ride receipt
    */

  const redirectType = route.query?.redirectType || '';
  const rideId = route.query?.rideId || '';
  if (!redirectType) {
    return;
  }

  if (redirectType === 'profile') {
    router.push('/family/profile');
  } else if (redirectType === 'orderRide') {
    router.push('/family/ride-ordering?rideFormType=order');
  } else if (redirectType === 'scheduledRide') {
    router.push(rideId ? `/family/ride-ordering?rideId=${rideId}` : '/family/ride-ordering');
  }
};

const getCardsList = () => {
  let items = util.getSideMenuItems(dashboardCategory.value);
  widgetCards.value = items
    .filter((item: any) => !excludedPages.value.includes(item.path))
    .filter((item: any) => {
      if (isPartnerDashboard.value) {
        return !item.partnersToExclude?.includes(partnerCode?.value);
      } else {
        return item;
      }
    });
};

watch(dashboardCategory, getCardsList, {
  immediate: true,
});

watch(partnerId, getCardsList, {
  deep: true,
  immediate: true,
});

onMounted(async () => {
  await nextTick();
  util.moveToTop();

  if (isFamilyDashboard.value) {
    redirectToSpecificFamilyDashboardPage();
  }
});
</script>

<style lang="scss">
.overview {
  ul {
    padding-left: 0;
    list-style: none;
  }
  .card-item {
    position: relative;
    margin-bottom: 20px;
    min-height: 120px;

    .card.clickable {
      cursor: pointer;

      &:active {
        box-shadow: none;
        top: 3px;
        left: 3px;
      }
    }

    a {
      text-decoration: none;
    }
    .card-stats .card-body .numbers .card-category {
      color: #004c4c;
      @media (max-width: 576px) {
        font-size: 20px;
      }
    }
  }
}
</style>
